import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiClientService } from '../../core/api/api-client.service';
import {
  LessonLearningResponse,
  LearningLessonSummary,
  LessonQuizResultResponse,
  LearningLessonVideo,
  LearningQuestion
} from '../../core/models/portal.models';

@Component({
  selector: 'app-lesson-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './lesson-page.component.html'
})
export class LessonPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly detail = signal<LessonLearningResponse | null>(null);
  protected readonly selectedOptions = signal<Record<number, number>>({});
  protected readonly result = signal<LessonQuizResultResponse | null>(null);
  protected readonly error = signal<string | null>(null);
  protected readonly saving = signal(false);
  protected readonly selectedVideoLanguage = signal<string | null>(null);

  private enrollmentId = 0;
  private lessonId = 0;

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.enrollmentId = Number(params.get('enrollmentId'));
      this.lessonId = Number(params.get('lessonId'));
      this.result.set(null);
      this.error.set(null);
      this.selectedOptions.set({});
      this.load();
    });

    this.route.queryParamMap.subscribe((params) => {
      this.selectedVideoLanguage.set(params.get('video_language'));
      this.syncSelectedVideoLanguage();
    });
  }

  protected markVideoWatched(item: LessonLearningResponse): void {
    this.saving.set(true);
    this.api.updateLessonProgress(item.enrollment.id, item.lesson.id, {
      watchedSeconds: item.lesson.durationSeconds,
      videoWatched: true
    }).subscribe({
      next: (detail) => {
        this.detail.set(detail);
        this.syncSelectedVideoLanguage(detail);
        this.saving.set(false);
      },
      error: () => {
        this.error.set('Die Videobestätigung konnte nicht gespeichert werden.');
        this.saving.set(false);
      }
    });
  }

  protected markSummaryRead(item: LessonLearningResponse): void {
    this.saving.set(true);
    this.api.updateLessonProgress(item.enrollment.id, item.lesson.id, {
      summaryRead: true
    }).subscribe({
      next: (detail) => {
        this.detail.set(detail);
        this.syncSelectedVideoLanguage(detail);
        this.saving.set(false);
      },
      error: () => {
        this.error.set('Die Zusammenfassung konnte nicht als gelesen markiert werden.');
        this.saving.set(false);
      }
    });
  }

  protected completeLesson(item: LessonLearningResponse): void {
    this.saving.set(true);
    this.api.updateLessonProgress(item.enrollment.id, item.lesson.id, {
      watchedSeconds: item.lesson.durationSeconds,
      videoWatched: true,
      summaryRead: true,
      completed: true
    }).subscribe({
      next: (detail) => {
        this.detail.set(detail);
        this.syncSelectedVideoLanguage(detail);
        this.saving.set(false);
      },
      error: () => {
        this.error.set('Die Lektion konnte nicht abgeschlossen werden.');
        this.saving.set(false);
      }
    });
  }

  protected selectOption(questionId: number, optionId: number): void {
    this.selectedOptions.update((current) => ({ ...current, [questionId]: optionId }));
  }

  protected selectedOptionId(questionId: number): number | null {
    return this.selectedOptions()[questionId] ?? null;
  }

  protected lessonAccessible(item: LessonLearningResponse, lesson: LearningLessonSummary): boolean {
    const nextLesson = item.enrollment.lessons.find((candidate) => candidate.id === item.enrollment.nextLessonId);
    return lesson.completed || nextLesson === undefined || lesson.lessonOrder <= nextLesson.lessonOrder;
  }

  protected submitQuiz(item: LessonLearningResponse): void {
    if (!this.allQuestionsAnswered(item.lesson.questions)) {
      this.error.set('Bitte beantworte alle Fragen.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.api.submitLessonQuiz(item.enrollment.id, item.lesson.id, {
      answers: item.lesson.questions.map((question) => ({
        questionId: question.id,
        selectedOptionId: this.selectedOptionId(question.id) ?? 0
      }))
    }).subscribe({
      next: (result) => {
        this.result.set(result);
        this.detail.set(result.lesson);
        this.syncSelectedVideoLanguage(result.lesson);
        this.saving.set(false);
      },
      error: () => {
        this.error.set('Der Wissens-Check konnte nicht ausgewertet werden.');
        this.saving.set(false);
      }
    });
  }

  protected questionResult(questionId: number) {
    return this.result()?.questions.find((question) => question.questionId === questionId) ?? null;
  }

  protected selectedVideo(item: LessonLearningResponse): LearningLessonVideo | null {
    const videos = item.lesson.availableVideos;
    if (!videos.length) {
      return null;
    }

    const selectedLanguage = this.selectedVideoLanguage();
    if (selectedLanguage) {
      const selectedVideo = videos.find((video) => video.language === selectedLanguage);
      if (selectedVideo) {
        return selectedVideo;
      }
    }

    return videos.find((video) => video.language === 'de') ?? videos[0];
  }

  protected selectedVideoLabel(item: LessonLearningResponse): string {
    const video = this.selectedVideo(item);
    if (!video) {
      return item.lesson.videoUrl ? 'Lektionsvideo' : '';
    }

    return video.title || this.videoLanguageLabel(video.language);
  }

  protected selectedVideoUrl(item: LessonLearningResponse): string | null {
    return this.selectedVideo(item)?.videoUrl || item.lesson.videoUrl;
  }

  protected selectedVideoIframeUrl(item: LessonLearningResponse): SafeResourceUrl | null {
    const video = this.selectedVideo(item);
    if (!video || !video.libraryId) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(this.selectedVideoUrl(item) || '');
  }

  protected selectedVideoIsEmbedded(item: LessonLearningResponse): boolean {
    return Boolean(this.selectedVideo(item)?.libraryId);
  }

  protected videoLanguageLabel(language: string): string {
    const labels: Record<string, string> = {
      de: 'Deutsch',
      en: 'Englisch',
      tr: 'Türkisch',
      ar: 'Arabisch',
      uk: 'Ukrainisch',
      ru: 'Russisch',
      pl: 'Polnisch',
      ps: 'Paschtu'
    };

    return labels[language] ?? language;
  }

  protected changeVideoLanguage(language: string): void {
    if (this.selectedVideoLanguage() === language) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { video_language: language },
      queryParamsHandling: 'merge'
    });
  }

  private load(): void {
    this.api.learningLesson(this.enrollmentId, this.lessonId).subscribe((detail) => {
      this.detail.set(detail);
      this.syncSelectedVideoLanguage(detail);
    });
  }

  private allQuestionsAnswered(questions: LearningQuestion[]): boolean {
    return questions.every((question) => Boolean(this.selectedOptionId(question.id)));
  }

  private syncSelectedVideoLanguage(detail: LessonLearningResponse | null = this.detail()): void {
    const videos = detail?.lesson.availableVideos ?? [];
    if (!videos.length) {
      this.selectedVideoLanguage.set(null);
      return;
    }

    const selectedLanguage = this.selectedVideoLanguage();
    if (selectedLanguage && videos.some((video) => video.language === selectedLanguage)) {
      return;
    }

    const defaultLanguage = videos.find((video) => video.language === 'de')?.language ?? videos[0].language;
    this.selectedVideoLanguage.set(defaultLanguage);
  }
}
